<template>
  <div class="printMe">
    <div class="bg-primary flex justify-content-center text-white mb-1 edit-class">
      {{ psc.type }}
      <span v-if="psc.type === PSCType.A3 || psc.type === PSCType.A5"
        >-{{ t("views.psc.Edit.psc") }}</span
      >
    </div>
    <div v-for="(areas, sectionName) in sections" :key="`section${sectionName}`" class="mb-3">
      <div class="flex m-0">
        <div :class="sectionColorClasses[sectionName]" class="w-30px bar">
          <span class="v-text">{{ sectionName }}</span>
        </div>
        <div class="w-100-30 mt-2">
          <div>
            <div
              v-for="(area, areaIndex) in areas"
              :key="`area${areaIndex}`"
              class="grid ml-1 mr-0 area notBreakInside"
              :class="{ 'border-top-1-imp': areaIndex === 0 }"
            >
              <div>
                <div>
                  <span class="numberCircle mb-1">{{ area.number }}</span>
                </div>
                <div class="verticalContainer">
                  <div class="vertical white-space-nowrap">
                    <span class=""
                      >{{ t(`components.psc.psc.area${area.numberKey ?? area.number}title`) }}
                    </span>
                  </div>
                  <div class="vertical-hidden white-space-nowrap">
                    <span>{{
                      t(`components.psc.psc.area${area.numberKey ?? area.number}title`)
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="col p-0">
                <div>
                  <span class="title ml-1">{{
                    t(`components.psc.psc.area${area.numberKey ?? area.number}titleHead`)
                  }}</span>
                </div>
                <component
                  :is="area.component"
                  :psc="psc"
                  :readonly="readonly"
                  class="bg-white componentElement component-class relative"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, watch, type PropType } from "vue";
import type { PSCEditTypeUnknow } from "../helper/PSCTypes";
import pscAreaConfig, { getDefaultPSC, sectionColorClasses } from "../helper/PSCAreaConfig";
import { PSCType } from "@/graphql";

defineProps({
  readonly: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const psc = defineModel("psc", {
  type: Object as PropType<PSCEditTypeUnknow>,
  required: true,
});

// only needed because 6k was disabled and kaizen has no values for 6
watch(
  psc,
  () => {
    pscAreaConfig[psc.value.type].forEach((area) => {
      if (psc.value[area.key] == null) {
        psc.value[area.key] = getDefaultPSC(psc.value.type)[area.key] as any;
      }
    });
  },
  {
    immediate: true,
  },
);

const sections = computed(() => {
  return pscAreaConfig[psc.value.type].reduce(
    (prev, curr) => {
      if (prev[curr.section] == null) {
        prev[curr.section] = [];
      }
      prev[curr.section].push(curr);
      return prev;
    },
    {} as Record<keyof typeof sectionColorClasses, (typeof pscAreaConfig)[PSCType.A3][number][]>,
  );
});

const { t } = useTranslation();
</script>

<style scoped>
.containerHeight {
  padding-bottom: 50px;
}

.footer {
  position: fixed;
  bottom: 0;
  left: calc(50% + 13px);
  transform: translateX(-50%);
  z-index: 11;
  background: #fff;
  padding-top: 0.3rem;
  padding-bottom: 1rem;
}

.component-class {
  height: calc(100% - 25px);
}

.edit-class {
  margin-left: -1px;
  width: calc(100% + 1px);
  font-size: 2.5rem;
  border: 1px solid black;
}

.ml-m025 {
  margin-left: -0.25rem;
}

.col-1-5 {
  max-width: 3.66%;
}

.col-12-5 {
  max-width: calc(97.494% - 5px);
}

.area {
  padding: 1px;
  background-color: rgb(215 215 215);
  margin-left: 1px;
  border: 1px solid black;
  padding-bottom: 5px;
}

.numberCircle {
  height: 23px;
  width: 23px;
  border: 1px solid rgb(0 160 245 / 100%);
  background-color: #fff;
  border-radius: 50%;
  text-align: center;
  vertical-align: middle;
  display: inline-block;
  margin: 2px;
}

.verticalContainer {
  height: calc(100% - 26px);
  position: relative;
}

.vertical {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  position: absolute;
  bottom: 8px;
  right: 2px;
  left: 2px;
}

.vertical-hidden {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  color: transparent;
  padding-top: 5px;
  user-select: none;
}

.title {
  font-size: small;
  font-weight: bold;
}

.bar {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  border: 1px solid;
}

.w-30px {
  width: 25px;
}

.v-text {
  padding-top: 5px;
}

.w-100-30 {
  width: calc(100% - 25px);
}

.border-top-1-imp {
  border-top: 1px solid black !important;
}

.cursor-auto {
  cursor: auto;
}

@media print {
  .printMe {
    margin: 0;
    border: initial;
    border-radius: initial;
    size: auto;
    page-break-after: avoid;
  }
}
</style>
<style lang="scss">
@media print {
  .notBreakInside {
    page-break-inside: avoid;
  }

  .navbar {
    display: none !important;
  }

  .printMeRight > div > div > div {
    > button {
      display: none;
    }

    > label {
      padding-left: 0.75rem !important;
    }
  }

  button {
    visibility: hidden;
  }

  .hideMeOnPrint {
    visibility: hidden;
  }

  input::placeholder {
    visibility: hidden;
  }

  .multiselect__placeholder {
    color: transparent;
  }
}

.border-grey .vs__dropdown-toggle {
  border: 1px solid var(--p-gray-200);
  height: 40px;
}

[contentEditable="true"]:empty:not(:focus)::before {
  content: attr(placeholder);
  color: var(--p-gray-500);
  font-size: 14px;
}

.placeholder-text-style {
  font-size: 14px;
}

.psc-error .multiselect__tags {
  border: 1px solid var(--p-lisa-invalid);
}

.psc-error .vs__dropdown-toggle {
  border: 1px solid var(--p-lisa-invalid);
}

.psc-edit .custom__tag:first-child {
  color: var(--p-gray-800);
  font-size: 1rem;
}

.psc-edit .multiselect__tags {
  padding-top: 0.35rem;
}
</style>
